/* eslint-disable no-restricted-globals */
import { useState, useEffect } from 'react';

interface ShareButtonPosition {
  top: number;
  right: number;
}

export const useDynamicShareButtonPosition = () => {
  const [position, setPosition] = useState<ShareButtonPosition>({
    top: 75,
    right: 20,
  });

  const calculateShareButtonPosition = (): ShareButtonPosition => {
    const isLandscape = window.innerWidth > window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isIPad13Inch = window.innerWidth > 1000;

    // Safari toolbar icon measurements
    const iconSize = 48; // Safari icons are 44x44px (not 48x48)
    const iconGap = isLandscape || isIPad13Inch ? 16 : 4;
    const toolbarPadding = 16; // Padding from screen edge

    const shareButtonFromRight =
      toolbarPadding + iconSize * 3 + iconGap * 2 + iconSize / 2;

    // Safari address bar height
    const safariAddressBarHeight = 16;
    const shareButtonFromTop = safariAddressBarHeight / 2; // Center of address bar

    const modalTop = safariAddressBarHeight + 8; // Just below address bar
    const modalRight = shareButtonFromRight - 120; // Account for modal width (~280px), center it near Share button

    console.log('Share Button Calculation:', {
      viewportWidth,
      isLandscape,
      iconSize,
      iconGap,
      toolbarPadding,
      shareButtonFromRight,
      shareButtonFromTop,
      safariAddressBarHeight,
      modalTop,
      modalRight,
      actualShareButtonX: viewportWidth - shareButtonFromRight,
      actualModalX: viewportWidth - modalRight,
    });

    return {
      top: modalTop,
      right: modalRight, // Ensure minimum distance from edge
    };
  };

  const updatePosition = () => {
    const newPosition = calculateShareButtonPosition();
    setPosition(newPosition);
  };

  useEffect(() => {
    // Initial calculation
    updatePosition();

    // Recalculate on orientation change
    const handleOrientationChange = () => {
      // Delay to allow Safari UI to settle
      setTimeout(updatePosition, 300);
    };

    // Recalculate on resize (Safari UI changes)
    const handleResize = () => {
      // Debounce resize events
      setTimeout(updatePosition, 100);
    };

    // Recalculate when viewport changes (Safari minimal UI)
    const handleScroll = () => {
      // Throttle scroll events
      clearTimeout((window as any).scrollTimeout);
      (window as any).scrollTimeout = setTimeout(updatePosition, 150);
    };

    // Listen for various events that might change Safari UI
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen for focus events (keyboard appearance can change UI)
    window.addEventListener('focusin', updatePosition);
    window.addEventListener('focusout', updatePosition);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('focusin', updatePosition);
      window.removeEventListener('focusout', updatePosition);
    };
  }, []);

  return {
    position,
    updatePosition,
  };
};
